import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

/**
 * Shared SEARCHABLE combobox for LONG DYNAMIC lists (accounts, contacts, users,
 * brands) — the systemic replacement for a shadcn <Select> / SelectField whose
 * option list can grow past the viewport. Built on Popover + Command (cmdk):
 *
 *   - the popover PORTALS to <body> and Radix collision-handles it, so it can
 *     never be clipped by an `overflow-hidden` ancestor (the Community/Publishing
 *     full-height shells, the tabbed account page) nor overflow the page;
 *   - a search box filters the list (over label + description + keywords);
 *   - the list is height-capped with its OWN internal scroll (CommandList);
 *   - keyboard nav (↑/↓ + Enter, Esc to close) comes from cmdk for free.
 *
 * The API deliberately mirrors SelectField (value / onValueChange / options /
 * placeholder / size / id / aria-label / disabled), so converting a long-list
 * SelectField is near-drop-in — plus searchPlaceholder + emptyText. Short FIXED
 * selects (status, currency, account type, …) stay on SelectField; this is only
 * for lists long enough to overflow.
 *
 * `value === ''` keeps SelectField's "none/any" semantics: pass an option with
 * `value: ''` (e.g. "No parent (root)", "Any brand") and it renders + selects
 * like any other. Rendering is capped (MAX_RENDERED) so a huge list stays snappy;
 * a "refine your search" footer appears when the filter is truncated.
 */

export type ComboboxOption = {
  value: string;
  label: string;
  // Optional secondary line (brand name, email, account type) — also searched.
  description?: string;
  // Extra searchable text not shown (e.g. an id, a slug).
  keywords?: string;
  disabled?: boolean;
};

// cmdk requires a non-empty, unique item value; map the "none" option's '' to a
// sentinel for the item identity only (onValueChange still emits the real '').
const EMPTY_SENTINEL = '__combobox_empty__';
const MAX_RENDERED = 100;

/**
 * Pure search filter (case-insensitive substring over label + description +
 * keywords) with a render cap. Exported + tested so the search behaviour is
 * verifiable without driving the popover in jsdom.
 */
export function filterComboboxOptions(
  options: readonly ComboboxOption[],
  query: string,
  max = MAX_RENDERED,
): { filtered: ComboboxOption[]; truncated: boolean } {
  const q = query.trim().toLowerCase();
  const matches =
    q === ''
      ? options
      : options.filter((o) =>
          `${o.label} ${o.description ?? ''} ${o.keywords ?? ''}`.toLowerCase().includes(q),
        );
  return { filtered: matches.slice(0, max), truncated: matches.length > max };
}

export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  size = 'default',
  className,
  contentClassName,
  id,
  disabled,
  'aria-label': ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  size?: 'sm' | 'default';
  className?: string;
  contentClassName?: string;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.value === value);

  const { filtered, truncated } = useMemo(
    () => filterComboboxOptions(options, query),
    [options, query],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'flex items-center justify-between gap-2 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            size === 'sm' ? 'h-8 w-auto min-w-[7rem] px-2.5' : 'h-10 w-full px-3 py-2',
            className,
          )}
        >
          <span className={cn('line-clamp-1 text-left', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-[--radix-popover-trigger-width] min-w-[14rem] p-0', contentClassName)}
      >
        {/* shouldFilter={false}: we filter in JS (over label + description + keywords)
            and cap rendering; cmdk still drives keyboard nav over the rendered items. */}
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {filtered.map((o) => (
              <CommandItem
                key={o.value === '' ? EMPTY_SENTINEL : o.value}
                value={o.value === '' ? EMPTY_SENTINEL : o.value}
                disabled={o.disabled}
                onSelect={() => {
                  onValueChange(o.value);
                  setOpen(false);
                  setQuery('');
                }}
              >
                <Check
                  className={cn('h-4 w-4 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.description && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {o.description}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
            {truncated && (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Showing first {MAX_RENDERED} — refine your search to narrow the list.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
